import { IReoEncryptKey } from '@reolink-cloud/interface';
import { algo } from 'crypto-js';
import Base64 from 'crypto-js/enc-base64';
import WordArray from 'crypto-js/lib-typedarrays';
import AES from 'crypto-js/aes';
import CFB from 'crypto-js/mode-cfb';
import NoPadding from 'crypto-js/pad-nopadding';
import PBKDF2 from 'crypto-js/pbkdf2';
import { IKeyProvider } from './keyProvider';

const KEY_LENGTH = 32;
const IV_LENGTH = 16;
const GCM_IV_LENGTH = 12;

interface IKeyMaterial {
    key: number[];
    iv: number[];
}

export const wordArrayToUint8Array = ( words: number[], sigBytes?: number ): Uint8Array => {

    const totalBytes = sigBytes ?? words.length * 4;
    if ( totalBytes > words.length * 4 ) {

        throw new Error( `Invalid sigBytes: ${totalBytes} exceeds maximum ${words.length * 4}` );

    }

    const u8 = new Uint8Array( totalBytes );
    let bytePos = 0;

    for ( let wordIndex = 0; bytePos < totalBytes; wordIndex++ ) {

        const word = words[wordIndex];
        for ( let offset = 24; offset >= 0 && bytePos < totalBytes; offset -= 8 ) {

            u8[bytePos++] = ( word >>> offset ) & 0xff;

        }

    }

    return u8;

};

export const toArrayBuffer = ( view: Uint8Array ): ArrayBuffer => {

    const buffer = view.buffer as ArrayBuffer;

    if ( view.byteOffset === 0 && view.byteLength === buffer.byteLength ) {

        return buffer;

    }

    return buffer.slice( view.byteOffset, view.byteOffset + view.byteLength );

};

const deriveKey = async (
    encrypt: IReoEncryptKey,
    keySeed: string,
    keyProvider: IKeyProvider
): Promise<IKeyMaterial> => {

    const salt = await keyProvider.getSalt({
        devicePublicKey: encrypt.devicePublicKey,
        cmkId: encrypt.cmkId
    });

    const keyIv = PBKDF2(
        keySeed,
        Base64.parse( salt ),
        {
            keySize: ( KEY_LENGTH + IV_LENGTH ) / 4,
            iterations: encrypt.iterations,
            hasher: algo.SHA256
        }
    );

    const key = keyIv.words.slice( 0, KEY_LENGTH / 4 );
    const iv = keyIv.words.slice( KEY_LENGTH / 4, KEY_LENGTH / 4 + IV_LENGTH / 4 );

    return { key, iv };

};

const decryptAES256CFB = (
    encryptedData: Uint8Array,
    key: number[],
    iv: number[]
): Uint8Array => {

    const words: number[] = [];
    for ( let i = 0; i < encryptedData.length; i += 4 ) {

        const word =
            (( encryptedData[i] || 0 ) << 24 ) |
            (( encryptedData[i + 1] || 0 ) << 16 ) |
            (( encryptedData[i + 2] || 0 ) << 8 ) |
            ( encryptedData[i + 3] || 0 );
        words.push( word >>> 0 );

    }

    const ciphertextWordArray = WordArray.create( words, encryptedData.length );
    const keyWordArray = WordArray.create( key );
    const ivWordArray = WordArray.create( iv );

    const decrypted = AES.decrypt(
        ciphertextWordArray.toString( Base64 ),
        keyWordArray, {
            iv: ivWordArray,
            mode: CFB,
            padding: NoPadding,
        });

    return wordArrayToUint8Array( decrypted.words, decrypted.sigBytes );

};

const decryptAES256GCM = async (
    encryptedData: Uint8Array,
    key: number[],
    iv: number[]
): Promise<Uint8Array> => {

    const keyBytes = wordArrayToUint8Array( key, KEY_LENGTH );
    const ivBytes = wordArrayToUint8Array( iv, GCM_IV_LENGTH );

    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        toArrayBuffer( keyBytes ),
        { name: 'AES-GCM' },
        false,
        ['decrypt']
    );

    try {

        const decryptedBuffer = await crypto.subtle.decrypt(
            {
                name: 'AES-GCM',
                iv: toArrayBuffer( ivBytes ),
            },
            cryptoKey,
            toArrayBuffer( encryptedData )
        );

        return new Uint8Array( decryptedBuffer );

    } catch ( error ) {

        console.error( error );
        throw new Error( `AES-256-GCM decryption failed: ${error instanceof Error ? error.message : String( error )}` );

    }

};

/**
 * 使用注入的 `keyProvider` 派生密钥，并按 `encrypt.algo` 解密整段数据。
 *
 * Service Worker 与主线程降级路径共享本函数；区别仅在于传入不同的 `IKeyProvider` 实现。
 */
export const decryptArrayBuffer = async (
    data: Uint8Array,
    encrypt: IReoEncryptKey,
    keySeed: string,
    keyProvider: IKeyProvider
): Promise<Uint8Array> => {

    const { key, iv } = await deriveKey( encrypt, keySeed, keyProvider );

    switch ( encrypt.algo ) {

        case 'AES-256-CFB':
            return decryptAES256CFB( data, key, iv );
        case 'AES-256-GCM':
            return decryptAES256GCM( data, key, iv );
        default:
            throw new Error( `Unsupported encryption algorithm: ${encrypt.algo}` );

    }

};
